import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Bell, X, CheckCheck, AlertTriangle, Info, AlertCircle,
  RefreshCw, Loader2, Settings, Mail, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";

const SEV_CONFIG = {
  high:    { icon: AlertCircle,  color: "#DC2626", bg: "bg-red-50",    border: "border-red-200",    badge: "bg-red-600",    label: "Urgente" },
  warning: { icon: AlertTriangle,color: "#D97706", bg: "bg-amber-50",  border: "border-amber-200",  badge: "bg-amber-500",  label: "Aviso" },
  info:    { icon: Info,         color: "#0891B2", bg: "bg-blue-50",   border: "border-blue-200",   badge: "bg-blue-500",   label: "Info" },
};

function AlertCard({ alert, onRead, onDismiss }) {
  const cfg = SEV_CONFIG[alert.severity] || SEV_CONFIG.info;
  const Icon = cfg.icon;
  return (
    <div className={`${cfg.bg} border ${cfg.border} rounded-xl p-3.5 transition-all ${alert.is_read ? "opacity-60" : ""}`}>
      <div className="flex gap-3">
        <Icon size={16} style={{ color: cfg.color, flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.badge} text-white`}>{cfg.label}</span>
            <button data-testid={`dismiss-alert-${alert.id}`} onClick={() => onDismiss(alert.id)}
              className="text-slate-300 hover:text-slate-600 transition-colors flex-shrink-0">
              <X size={12} />
            </button>
          </div>
          <p className="font-semibold text-slate-800 text-sm leading-tight">{alert.title}</p>
          <p className="text-slate-500 text-xs mt-0.5 leading-snug">{alert.message}</p>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-slate-400">{alert.contract_name} · {(alert.created_at || "").slice(0, 10)}</span>
            {!alert.is_read && (
              <button onClick={() => onRead(alert.id)}
                className="text-xs text-slate-400 hover:text-[#007934] transition-colors underline">
                Marcar leída
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertSettings({ onClose }) {
  const { authAxios } = useAuth();
  const [settings, setSettings] = useState({ email_enabled: false, recipient_emails: [], min_severity: "warning" });
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authAxios.get("/alerts/settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const addEmail = () => {
    if (!newEmail || !newEmail.includes("@")) return;
    setSettings(s => ({ ...s, recipient_emails: [...new Set([...s.recipient_emails, newEmail.trim()])] }));
    setNewEmail("");
  };

  const removeEmail = (email) => setSettings(s => ({ ...s, recipient_emails: s.recipient_emails.filter(e => e !== email) }));

  const save = async () => {
    setSaving(true);
    try {
      await authAxios.put("/alerts/settings", settings);
      toast.success("Configuración de alertas guardada");
      onClose();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 space-y-4 border-t border-slate-100">
      <p className="font-bold text-slate-800 flex items-center gap-2">
        <Mail size={14} className="text-[#007934]" /> Notificaciones por email
      </p>
      <label className="flex items-center gap-3 cursor-pointer">
        <div className={`w-10 h-5 rounded-full transition-all ${settings.email_enabled ? "bg-[#007934]" : "bg-slate-300"}`}
          onClick={() => setSettings(s => ({ ...s, email_enabled: !s.email_enabled }))}>
          <div className={`w-4 h-4 bg-white rounded-full shadow transition-all m-0.5 ${settings.email_enabled ? "translate-x-5" : ""}`} />
        </div>
        <span className="text-sm text-slate-600">Activar notificaciones por email</span>
      </label>
      {settings.email_enabled && (
        <>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Severidad mínima para enviar email</label>
            <select value={settings.min_severity} onChange={e => setSettings(s => ({ ...s, min_severity: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white">
              <option value="high">Solo urgentes</option>
              <option value="warning">Avisos y urgentes</option>
              <option value="info">Todas las alertas</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Destinatarios</label>
            <div className="flex gap-2">
              <input value={newEmail} onChange={e => setNewEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addEmail()}
                placeholder="email@empresa.com" type="email"
                className="flex-1 border border-slate-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#007934]/30" />
              <button onClick={addEmail} className="bg-[#007934] text-white px-3 py-1.5 rounded-xl text-sm hover:bg-[#005c27] transition-all">
                <Plus size={14} />
              </button>
            </div>
            <div className="mt-2 space-y-1">
              {settings.recipient_emails.map(e => (
                <div key={e} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-1.5 text-xs">
                  <span className="text-slate-700">{e}</span>
                  <button onClick={() => removeEmail(e)} className="text-slate-400 hover:text-red-500"><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
      <button onClick={save} disabled={saving}
        className="w-full bg-[#007934] hover:bg-[#005c27] text-white py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 flex items-center justify-center gap-2">
        {saving ? <Loader2 size={14} className="animate-spin" /> : null} Guardar configuración
      </button>
    </div>
  );
}

export default function AlertBell() {
  const { authAxios } = useAuth();
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const panelRef = useRef();
  const pollRef = useRef();

  const fetchAlerts = useCallback(async (includeRead = false) => {
    try {
      const r = await authAxios.get(`/alerts?include_read=${includeRead}&include_dismissed=false&limit=50`);
      setAlerts(r.data.alerts || []);
      setUnread(r.data.total_unread || 0);
    } catch {}
  }, [authAxios]);

  // Poll unread count every 2 minutes
  useEffect(() => {
    fetchAlerts(false);
    pollRef.current = setInterval(() => fetchAlerts(false), 120_000);
    return () => clearInterval(pollRef.current);
  }, [fetchAlerts]);

  // When panel opens, load all (read + unread) and trigger generation
  useEffect(() => {
    if (open) {
      setLoading(true);
      authAxios.get("/alerts?include_read=true&include_dismissed=false&limit=50")
        .then(r => { setAlerts(r.data.alerts || []); setUnread(r.data.total_unread || 0); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleRead = async (id) => {
    await authAxios.post(`/alerts/${id}/read`).catch(() => {});
    setAlerts(a => a.map(x => x.id === id ? { ...x, is_read: true } : x));
    setUnread(u => Math.max(0, u - 1));
  };

  const handleDismiss = async (id) => {
    await authAxios.post(`/alerts/${id}/dismiss`).catch(() => {});
    setAlerts(a => a.filter(x => x.id !== id));
    const dismissed = alerts.find(x => x.id === id);
    if (dismissed && !dismissed.is_read) setUnread(u => Math.max(0, u - 1));
  };

  const handleReadAll = async () => {
    await authAxios.post("/alerts/read-all").catch(() => {});
    setAlerts(a => a.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await authAxios.post("/alerts/generate");
      toast.success("Generando alertas en segundo plano...");
      setTimeout(() => fetchAlerts(true).then(() => setGenerating(false)), 3000);
    } catch { setGenerating(false); }
  };

  const high = alerts.filter(a => a.severity === "high");
  const warn = alerts.filter(a => a.severity === "warning");
  const info = alerts.filter(a => a.severity === "info");

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        data-testid="alert-bell-btn"
        onClick={() => { setOpen(o => !o); setShowSettings(false); }}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-all"
      >
        <Bell size={18} className="text-slate-500" />
        {unread > 0 && (
          <span data-testid="alert-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="alert-panel"
          className="absolute right-0 top-11 w-[400px] bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-50 animate-fade-in-up"
          style={{ maxHeight: "85vh", overflowY: "auto" }}
        >
          {/* Panel header */}
          <div className="sticky top-0 bg-white px-4 py-3 border-b border-slate-100 flex items-center justify-between z-10">
            <div>
              <p className="font-bold text-slate-800 text-sm">Alertas operativas</p>
              {unread > 0
                ? <p className="text-xs text-red-500 font-semibold">{unread} sin leer</p>
                : <p className="text-xs text-slate-400">Todo al día</p>}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button data-testid="mark-all-read-btn" onClick={handleReadAll} title="Marcar todas como leídas"
                  className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500 hover:text-[#007934] transition-all">
                  <CheckCheck size={15} />
                </button>
              )}
              <button data-testid="generate-alerts-btn" onClick={handleGenerate} disabled={generating} title="Generar alertas ahora"
                className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-500 hover:text-[#007934] transition-all">
                {generating ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              </button>
              <button data-testid="alert-settings-btn" onClick={() => setShowSettings(s => !s)} title="Configurar emails"
                className={`p-1.5 rounded-xl hover:bg-slate-100 transition-all ${showSettings ? "text-[#007934] bg-[#E6F4EA]" : "text-slate-500"}`}>
                <Settings size={15} />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-xl hover:bg-slate-100 text-slate-400">
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Settings */}
          {showSettings && <AlertSettings onClose={() => setShowSettings(false)} />}

          {/* Content */}
          <div className="p-3 space-y-2">
            {loading
              ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#007934]" /></div>
              : alerts.length === 0
              ? (
                <div className="text-center py-10">
                  <Bell size={32} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 font-medium text-sm">No hay alertas activas</p>
                  <button onClick={handleGenerate} disabled={generating}
                    className="mt-3 text-xs text-[#007934] underline hover:no-underline">
                    {generating ? "Generando..." : "Generar ahora"}
                  </button>
                </div>
              )
              : (
                <>
                  {high.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider px-1 mb-1.5">Urgentes ({high.length})</p>
                      {high.map(a => <AlertCard key={a.id} alert={a} onRead={handleRead} onDismiss={handleDismiss} />)}
                    </div>
                  )}
                  {warn.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider px-1 mb-1.5 mt-3">Avisos ({warn.length})</p>
                      {warn.map(a => <AlertCard key={a.id} alert={a} onRead={handleRead} onDismiss={handleDismiss} />)}
                    </div>
                  )}
                  {info.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider px-1 mb-1.5 mt-3">Información ({info.length})</p>
                      {info.map(a => <AlertCard key={a.id} alert={a} onRead={handleRead} onDismiss={handleDismiss} />)}
                    </div>
                  )}
                </>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
